const MESSAGES = [
  { id: "PING",              dir: "ext → host", desc: "Health check — confirms native host is running and responsive" },
  { id: "GET_CREDENTIALS",   dir: "ext → host", desc: "Request matching credentials for a domain from the vault" },
  { id: "CHECK_CREDENTIAL",  dir: "ext → host", desc: "Check whether a username/password pair already exists in the vault" },
  { id: "SAVE_CREDENTIAL",   dir: "ext → host", desc: "Encrypt and save a new credential to the SQLite vault" },
  { id: "UPDATE_CREDENTIAL", dir: "ext → host", desc: "Encrypt and update a changed password for an existing entry" },
  { id: "FILL_CREDENTIALS",  dir: "host → ext", desc: "Return decrypted username and password for autofill into DOM fields" },
];

const BROWSERS = [
  {
    name: "Chrome / Edge",
    manifest: "Manifest V3",
    badge: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    acc: "text-sky-300",
    features: ["Service worker background script", "chrome.runtime.connectNative()", "Floating autofill button via content script", "MutationObserver for SPA login detection", "sessionStorage for redirect-surviving capture"],
    install: "Native host manifest registered at HKCU by Inno Setup installer",
  },
  {
    name: "Firefox",
    manifest: "Manifest V2",
    badge: "bg-orange-500/20 text-orange-300 border-orange-500/40",
    acc: "text-orange-300",
    features: ["Persistent background page", "browser.runtime.connectNative()", "Same content script logic as Chrome/Edge", "Firefox enterprise policy auto-installs extension", "Separate allowed_extensions manifest entry"],
    install: "Enterprise policy JSON written by Inno Setup — no manual extension install needed",
  },
];

export default function ExtensionPipeline() {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">EXTENSION PIPELINE</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Browser extension + native messaging</h4>
        <p className="mt-1 text-xs text-slate-400">
          Chrome/Edge (MV3) and Firefox (MV2) extensions communicate with the desktop vault via Chrome Native Messaging — a stdio-based JSON protocol that keeps credentials local and never exposes them to web content or remote servers.
        </p>
      </div>

      {/* Architecture SVG */}
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/30 p-4">
        <p className="mb-3 font-mono text-[10px] tracking-[0.18em] text-slate-500">MESSAGING ARCHITECTURE</p>
        <svg viewBox="0 0 620 130" width="100%" style={{ minWidth: 500 }}>
          <defs>
            <marker id="ext-arr" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={5} markerHeight={5} orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="rgba(255,255,255,.2)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </marker>
          </defs>

          {/* Web page */}
          <rect x={10} y={35} width={110} height={55} rx={7} fill="rgba(100,116,139,.1)" stroke="rgba(100,116,139,.35)" strokeWidth={0.8} />
          <text x={65} y={58} textAnchor="middle" fontSize={10} fontWeight={600} fill="#f0f4ff" fontFamily="system-ui">Login Page</text>
          <text x={65} y={72} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,.4)" fontFamily="system-ui">Content script</text>
          <text x={65} y={84} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,.4)" fontFamily="system-ui">+ floating button</text>

          {/* Arrow: page → bg */}
          <line x1={120} y1={62} x2={148} y2={62} stroke="rgba(255,255,255,.2)" strokeWidth={1} markerEnd="url(#ext-arr)" />
          <text x={134} y={57} textAnchor="middle" fontSize={7.5} fill="rgba(255,255,255,.25)" fontFamily="monospace">msg</text>

          {/* Background script */}
          <rect x={150} y={20} width={120} height={85} rx={7} fill="rgba(56,189,248,.08)" stroke="rgba(56,189,248,.35)" strokeWidth={0.8} />
          <text x={210} y={42} textAnchor="middle" fontSize={10} fontWeight={600} fill="#38bdf8" fontFamily="system-ui">Background</text>
          <text x={210} y={56} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,.4)" fontFamily="system-ui">Service worker</text>
          <text x={210} y={68} textAnchor="middle" fontSize={8.5} fill="rgba(56,189,248,.6)" fontFamily="system-ui">(MV3 Chrome/Edge)</text>
          <text x={210} y={82} textAnchor="middle" fontSize={8.5} fill="rgba(251,146,60,.6)" fontFamily="system-ui">Background page</text>
          <text x={210} y={93} textAnchor="middle" fontSize={8.5} fill="rgba(251,146,60,.6)" fontFamily="system-ui">(MV2 Firefox)</text>

          {/* Arrow: bg ↔ native host */}
          <line x1={270} y1={55} x2={320} y2={55} stroke="rgba(34,211,238,.4)" strokeWidth={1.2} markerEnd="url(#ext-arr)" />
          <line x1={320} y1={68} x2={270} y2={68} stroke="rgba(34,211,238,.4)" strokeWidth={1.2} markerEnd="url(#ext-arr)" />
          <text x={295} y={50} textAnchor="middle" fontSize={7.5} fill="rgba(34,211,238,.6)" fontFamily="monospace">JSON stdio</text>
          <text x={295} y={78} textAnchor="middle" fontSize={7.5} fill="rgba(34,211,238,.6)" fontFamily="monospace">Native Msg</text>

          {/* Native host */}
          <rect x={320} y={20} width={120} height={55} rx={7} fill="rgba(167,139,248,.08)" stroke="rgba(167,139,248,.35)" strokeWidth={0.8} />
          <text x={380} y={44} textAnchor="middle" fontSize={10} fontWeight={600} fill="#a78bfa" fontFamily="system-ui">Native Host</text>
          <text x={380} y={58} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,.4)" fontFamily="system-ui">securevault_host.exe</text>
          <text x={380} y={70} textAnchor="middle" fontSize={8.5} fill="rgba(167,139,248,.5)" fontFamily="system-ui">PyInstaller bundle</text>

          {/* Arrow: host ↔ vault */}
          <line x1={440} y1={47} x2={470} y2={47} stroke="rgba(52,211,153,.35)" strokeWidth={1.2} markerEnd="url(#ext-arr)" />
          <line x1={470} y1={58} x2={440} y2={58} stroke="rgba(52,211,153,.35)" strokeWidth={1.2} markerEnd="url(#ext-arr)" />

          {/* Vault */}
          <rect x={470} y={20} width={130} height={55} rx={7} fill="rgba(52,211,153,.08)" stroke="rgba(52,211,153,.35)" strokeWidth={0.8} />
          <text x={535} y={44} textAnchor="middle" fontSize={10} fontWeight={600} fill="#34d399" fontFamily="system-ui">Vault (Desktop)</text>
          <text x={535} y={58} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,.4)" fontFamily="system-ui">AES-256-GCM SQLite</text>
          <text x={535} y={70} textAnchor="middle" fontSize={8.5} fill="rgba(52,211,153,.5)" fontFamily="system-ui">In-memory key</text>

          {/* sessionStorage note */}
          <rect x={10} y={100} width={260} height={24} rx={5} fill="rgba(251,191,36,.05)" stroke="rgba(251,191,36,.2)" strokeWidth={0.8} />
          <text x={140} y={115} textAnchor="middle" fontSize={8.5} fill="rgba(251,191,36,.6)" fontFamily="system-ui">sessionStorage survives redirect — enables save/update detection on next page load</text>
        </svg>
      </div>

      {/* Browser cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        {BROWSERS.map((b) => (
          <div key={b.name} className={`rounded-xl border p-3 ${b.badge}`}>
            <div className="mb-2 flex items-center gap-2">
              <p className="text-sm font-bold">{b.name}</p>
              <span className={`ml-auto rounded border px-1.5 py-0.5 font-mono text-[9px] ${b.badge}`}>{b.manifest}</span>
            </div>
            <ul className="mb-2 space-y-1">
              {b.features.map((f) => (
                <li key={f} className="flex gap-1.5 text-[11px]">
                  <span className={`mt-0.5 shrink-0 ${b.acc}`}>›</span>
                  <span className="text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
            <p className="rounded bg-black/20 px-2 py-1 font-mono text-[9px] text-slate-400">{b.install}</p>
          </div>
        ))}
      </div>

      {/* Message protocol */}
      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
        <p className="mb-2 font-mono text-[9px] tracking-[0.15em] text-slate-500">NATIVE MESSAGING PROTOCOL</p>
        <div className="space-y-1.5">
          {MESSAGES.map(({ id, dir, desc }) => (
            <div key={id} className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
              <span className="min-w-[140px] font-mono text-[10px] font-bold text-cyan-300">{id}</span>
              <span className="rounded border border-white/10 bg-black/20 px-1.5 font-mono text-[8px] text-slate-500 whitespace-nowrap">{dir}</span>
              <span className="text-[11px] text-slate-400">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
